import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { loadMyRsvps } from './eventRsvpApi';
import EventRsvpPass from './EventRsvpPass';

const { FiDownload, FiInfo, FiPrinter } = FiIcons;

function escapeCsv(value) {
  let text = String(value ?? '');
  if (/^[\t\r ]*[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}

function downloadCsv(rows, memorialName) {
  const columns = ['event_id', 'memorial_id', 'event_title', 'event_date', 'status'];
  const csv = [
    columns.map(escapeCsv).join(','),
    ...rows.map((row) => columns.map((column) => escapeCsv(row?.[column])).join(',')),
  ].join('\r\n');
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  const slug = String(memorialName ?? 'events')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'events';

  anchor.href = url;
  anchor.download = `${slug}-my-rsvps.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function EventRsvpExport({ memorialName }) {
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [passes, setPasses] = useState([]);
  const [passesLoading, setPassesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadMyRsvps().then((result) => {
      if (cancelled) return;
      if (result?.status === 'success') {
        setPasses(Object.values(result?.map ?? {}).filter((row) => row?.status === 'yes'));
      }
      setPassesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExport = async () => {
    setLoading(true);
    setNotice('');
    const result = await loadMyRsvps();
    setLoading(false);

    if (result?.status !== 'success') {
      setNotice(result?.error || 'Your saved RSVPs could not be loaded.');
      return;
    }

    const rows = Object.values(result?.map ?? {}).filter((row) => row?.event_id);
    if (rows.length === 0) {
      setNotice('You have no saved RSVPs to export yet.');
      return;
    }

    downloadCsv(rows, memorialName);
    setNotice(`Exported ${rows.length} of your saved RSVPs.`);
  };

  const handlePrintAllPasses = () => {
    if (!passes.length) return;

    const cleanup = () => {
      document.body.classList.remove('printing-all-rsvp-passes');
      window.removeEventListener('afterprint', cleanup);
    };

    document.body.classList.add('printing-all-rsvp-passes');
    window.addEventListener('afterprint', cleanup, { once: true });
    window.print();
  };

  return (
    <section className="rounded-2xl border border-brand-dark bg-white p-5 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-serif text-accent-navy">Your RSVP export</h3>
          <p className="text-xs text-text-muted mt-1">Download a CSV of your own saved memorial-event RSVPs.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleExport}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-accent-navy text-white text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <SafeIcon icon={FiDownload} className="w-4 h-4" />
            {loading ? 'Preparing…' : 'Export My RSVPs'}
          </button>
          <button
            type="button"
            onClick={handlePrintAllPasses}
            disabled={passesLoading || passes.length === 0}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-brand-dark bg-white text-accent-navy text-[10px] font-black uppercase tracking-widest hover:border-accent-gold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <SafeIcon icon={FiPrinter} className="w-4 h-4" />
            Print all passes
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
        <SafeIcon icon={FiInfo} className="w-4 h-4 mt-0.5 shrink-0" />
        <p>Guest-list export is unavailable: the backend does not verify memorial-owner access to other attendees’ private RSVPs.</p>
      </div>

      <div className="border-t border-brand-light pt-4">
        <h4 className="text-sm font-serif text-accent-navy">Your QR RSVP passes</h4>
        <p className="mt-1 text-xs text-text-muted">Show your pass to the event team for visual check-in.</p>
        {passesLoading ? (
          <p className="mt-4 text-sm text-text-muted">Loading your passes…</p>
        ) : passes.length ? (
          <div className="rsvp-pass-batch-print-target mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {passes.map((pass, index) => (
              <EventRsvpPass
                key={pass?.id ?? `${pass?.event_id ?? 'event'}-${index}`}
                rsvp={pass}
                memorialName={memorialName}
              />
            ))}
          </div>
        ) : (
          <p className="mt-4 rounded-xl bg-brand-light px-4 py-3 text-sm text-text-muted">
            Confirm an RSVP as attending to get a QR pass.
          </p>
        )}
      </div>

      {notice && <p role="status" className="text-xs text-text-muted">{notice}</p>}
    </section>
  );
}